<script setup lang="ts">
import MainLayout from '../components/layouts/MainLayout.vue'
import { ref } from 'vue'

type LogLine = {
  t: string
  type: string
  details: Record<string, any>
}

const debugOutput = ref<string>('')
const inputValue = ref<string>('')

const MAX_LINES = 300
const logs = ref<LogLine[]>([])

function nowTime() {
  const d = new Date()
  const pad = (n: number, w = 2) => String(n).padStart(w, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${pad(
    d.getMilliseconds(),
    3
  )}`
}

function safeSelection(el: HTMLInputElement | null) {
  if (!el) return {}
  return {
    selectionStart: el.selectionStart,
    selectionEnd: el.selectionEnd,
    selectionDirection: el.selectionDirection,
  }
}

function pushLog(type: string, details: Record<string, any>) {
  logs.value.unshift({
    t: nowTime(),
    type,
    details,
  })

  if (logs.value.length > MAX_LINES) logs.value.length = MAX_LINES

  debugOutput.value = logs.value
    .map((l) => `${l.t}  ${l.type}\n${JSON.stringify(l.details, null, 2)}`)
    .join('\n\n' + '-'.repeat(60) + '\n\n')
}

function getInputEl(e: Event) {
  const t = e.target
  return t instanceof HTMLInputElement ? t : null
}

function onAnyEvent(e: Event) {
  const el = getInputEl(e)
  const base: Record<string, any> = {
    value: el?.value,
    ...safeSelection(el),
    isTrusted: e.isTrusted,
  }

  // Keyboard
  if (e instanceof KeyboardEvent) {
    pushLog(e.type, {
      ...base,
      key: e.key,
      code: e.code,
      repeat: e.repeat,
      ctrlKey: e.ctrlKey,
      shiftKey: e.shiftKey,
      altKey: e.altKey,
      metaKey: e.metaKey,
    })
    return
  }

  if (e instanceof InputEvent) {
    pushLog(e.type, {
      ...base,
      inputType: e.inputType,
      data: e.data,
      dataTransferTypes: e.dataTransfer ? Array.from(e.dataTransfer.types) : null,
      isComposing: e.isComposing,
    })
    return
  }

  if (e instanceof ClipboardEvent) {
    const text = e.clipboardData?.getData('text/plain') ?? e.clipboardData?.getData('text') ?? null

    pushLog(e.type, {
      ...base,
      clipboardText: text,
      types: e.clipboardData ? Array.from(e.clipboardData.types) : null,
    })
    return
  }

  if (e instanceof PointerEvent) {
    pushLog(e.type, {
      ...base,
      pointerType: e.pointerType,
      button: e.button,
      buttons: e.buttons,
      clientX: e.clientX,
      clientY: e.clientY,
    })
    return
  }

  if (e instanceof MouseEvent) {
    pushLog(e.type, {
      ...base,
      button: e.button,
      buttons: e.buttons,
      clientX: e.clientX,
      clientY: e.clientY,
    })
    return
  }

  pushLog(e.type, base)
}

function clearLogs() {
  logs.value = []
  debugOutput.value = ''
}

function sendDebugData() {
  const debugUrl = import.meta.env.VITE_DEBUG_URL
  if (!debugUrl) return
  fetch(debugUrl, {
    method: 'POST',
    body: JSON.stringify({
      message: debugOutput.value,
    }),
    headers: {
      'Content-type': 'application/json; charset=UTF-8',
    },
  })
}
</script>

<template>
  <MainLayout>
    <div class="d-flex align-items-center justify-content-between mb-12">
      <div>
        <label for="debugInput" class="form-label mb-4">Debug</label>
        <div class="text-muted small">
          Try typing, pasting, IME composition, Enter, arrow keys, selecting text, etc.
        </div>
      </div>

      <div class="d-flex flex-wrap gap-12">
        <button type="button" class="btn btn-outline-secondary btn-sm" @click="clearLogs">
          Clear
        </button>

        <button type="button" class="btn btn-outline-secondary btn-sm" @click="sendDebugData">
          Send
        </button>
      </div>
    </div>

    <div class="mb-12">
      <input
        id="debugInput"
        v-model="inputValue"
        class="form-control"
        type="text"
        autocomplete="off"
        spellcheck="false"
        @focus="onAnyEvent"
        @blur="onAnyEvent"
        @keydown="onAnyEvent"
        @keyup="onAnyEvent"
        @beforeinput="onAnyEvent"
        @input="onAnyEvent"
        @change="onAnyEvent"
        @compositionstart="onAnyEvent"
        @compositionupdate="onAnyEvent"
        @compositionend="onAnyEvent"
        @paste="onAnyEvent"
        @copy="onAnyEvent"
        @cut="onAnyEvent"
        @select="onAnyEvent"
        @pointerdown="onAnyEvent"
        @pointerup="onAnyEvent"
        @mousedown="onAnyEvent"
        @mouseup="onAnyEvent"
        @click="onAnyEvent"
      />
    </div>

    <div class="mb-12">
      <div class="small text-muted mb-4">
        Current v-model value:
        <code>{{ inputValue }}</code>
      </div>
    </div>

    <pre class="p-12 bg-light border rounded" style="max-height: 60vh; overflow: auto">{{
      debugOutput || 'No events yet.'
    }}</pre>
  </MainLayout>
</template>
